import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FaGithub, FaLinkedin, FaYoutube, FaFacebook, FaInstagram, FaBars, FaTimes, FaSun, FaMoon } from 'react-icons/fa'
import { Link, useLocation } from 'react-router'

const navLinks = [
  { label: 'Home',         href: '/'         },
  { label: 'About',        href: '/about'        },
  { label: 'Portfolio',    href: '/portfolio'    },
  { label: 'Skills',       href: '/skills'       },
  { label: 'Education',    href: '/education'    },
  { label: 'Certificates', href: '/certificates' },
  { label: 'Contact',      href: '/contact'      },
]
const socials = [
  { icon: FaGithub,    href: 'https://github.com/ShankarTheCoder',                 label: 'GitHub',    hoverColor: 'var(--text-1)', hoverBg: 'rgba(255,255,255,0.1)' },
  { icon: FaLinkedin,  href: 'https://linkedin.com/in/shankarthecoder',              label: 'LinkedIn',  hoverColor: '#0A66C2', hoverBg: 'rgba(10,102,194,0.1)' },
  { icon: FaYoutube,   href: 'https://www.youtube.com/@ShankarYt-20',               label: 'YouTube',   hoverColor: '#FF0000', hoverBg: 'rgba(255,0,0,0.1)' },
  { icon: FaFacebook,  href: 'https://www.facebook.com/shivshankarpandit93',        label: 'Facebook',  hoverColor: '#1877F2', hoverBg: 'rgba(24,119,242,0.1)' },
  { icon: FaInstagram, href: 'https://www.instagram.com/shivshankarpandit93/',      label: 'Instagram', hoverColor: '#E1306C', hoverBg: 'rgba(225,48,108,0.1)' },
]

export default function Navbar() {
  const [scrolled,    setScrolled]    = useState(false)
  const [mobileOpen,  setMobileOpen]  = useState(false)
  const [theme,       setTheme]       = useState(localStorage.getItem('theme') || 'dark')
  const location = useLocation()
  const currentPath = location.pathname

  useEffect(() => {
    if (theme === 'light') {
      document.documentElement.setAttribute('data-theme', 'light')
    } else {
      document.documentElement.removeAttribute('data-theme')
    }
    localStorage.setItem('theme', theme)
  }, [theme])

  const toggleTheme = () => setTheme(theme === 'dark' ? 'light' : 'dark')

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <>
      <motion.nav
        initial={{ y: -72, opacity: 0 }}
        animate={{ y: 0,   opacity: 1 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          background: scrolled ? 'var(--glass-bg)' : 'transparent',
          backdropFilter: scrolled ? 'blur(20px) saturate(180%)' : 'none',
          borderBottom: scrolled ? '1px solid var(--border)' : '1px solid transparent',
          boxShadow: scrolled ? '0 2px 24px rgba(0,0,0,0.08)' : 'none',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">

            {/* Logo + Socials */}
            <div className="flex items-center gap-5">
              <Link to="/" className="flex items-center gap-2.5 group" aria-label="Home">
                <div className="w-9 h-9 rounded-2xl flex items-center justify-center font-display font-black text-white text-base shadow-lg transition-transform group-hover:scale-110"
                  style={{ background: 'linear-gradient(135deg,var(--accent-1),var(--accent-2))' }}>
                  S
                </div>
                <span className="hidden sm:block font-display font-bold text-sm text-primary opacity-80">SSP</span>
              </Link>
              <div className="hidden lg:flex items-center gap-1">
                <motion.button onClick={toggleTheme} aria-label="Toggle Theme"
                  className="w-8 h-8 flex items-center justify-center rounded-xl transition-all duration-200 mr-2"
                  style={{ color: 'var(--text-3)' }}
                  whileHover={{ scale: 1.18, color: 'var(--accent-1)', background: 'rgba(59,130,246,0.1)' }}
                  whileTap={{ scale: 0.9 }}>
                  {theme === 'dark' ? <FaSun size={14} /> : <FaMoon size={14} />}
                </motion.button>
                {socials.map(({ icon: Icon, href, label, hoverColor, hoverBg }) => (
                  <motion.a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}
                    className="w-8 h-8 flex items-center justify-center rounded-xl transition-all duration-200"
                    style={{ color: 'var(--text-3)' }}
                    whileHover={{ scale: 1.18, color: hoverColor, background: hoverBg }}
                    whileTap={{ scale: 0.9 }}>
                    <Icon size={13} />
                  </motion.a>
                ))}
              </div>
            </div>

            {/* Nav links */}
            <div className="hidden md:flex items-center gap-0.5">
              {navLinks.map(({ label, href }) => (
                <motion.div key={label} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                  <Link to={href}
                    className="relative px-3.5 py-1.5 rounded-xl text-sm font-body font-medium transition-colors duration-200 block group"
                    style={{ color: currentPath === href ? 'var(--accent-1)' : 'var(--text-2)' }}>
                    {currentPath === href && (
                      <motion.span layoutId="nav-pill" className="absolute inset-0 rounded-xl"
                        style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)' }} />
                    )}
                    <span className="relative z-10 transition-all duration-300 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:via-purple-500 group-hover:to-pink-500">{label}</span>
                    <span 
                      className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-[2px] rounded-full transition-all duration-300 ease-out group-hover:w-3/4 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500"
                      style={{ boxShadow: '0 0 12px 2px rgba(168, 85, 247, 0.6)' }}
                    />
                  </Link>
                </motion.div>
              ))}

            </div>

            {/* Mobile controls */}
            <div className="flex md:hidden items-center gap-1.5">
              <motion.button onClick={toggleTheme} aria-label="Toggle Theme"
                className="w-9 h-9 flex items-center justify-center rounded-xl mr-1"
                style={{ color: 'var(--text-2)', background: 'var(--bg-hover)', border: '1px solid var(--border)' }}
                whileTap={{ scale: 0.9 }}>
                {theme === 'dark' ? <FaSun size={15} /> : <FaMoon size={15} />}
              </motion.button>
              <motion.button onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu"
                className="w-9 h-9 flex items-center justify-center rounded-xl"
                style={{ color: 'var(--text-2)', background: 'var(--bg-hover)', border: '1px solid var(--border)' }}
                whileTap={{ scale: 0.9 }}>
                {mobileOpen ? <FaTimes size={15} /> : <FaBars size={15} />}
              </motion.button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.22 }}
            className="fixed top-16 left-0 right-0 z-40 md:hidden"
            style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(20px)', borderBottom: '1px solid var(--border)' }}
          >
            <div className="px-4 py-4 space-y-1">
              {navLinks.map(({ label, href }) => (
                <Link key={label} to={href} onClick={() => setMobileOpen(false)}
                  className="w-full text-left px-4 py-3 rounded-2xl text-sm font-medium transition-all block"
                  style={{
                    color: currentPath === href ? 'var(--accent-1)' : 'var(--text-2)',
                    background: currentPath === href ? 'rgba(59,130,246,0.08)' : 'transparent',
                    border: `1px solid ${currentPath === href ? 'rgba(59,130,246,0.2)' : 'transparent'}`,
                  }}>
                  {label}
                </Link>
              ))}
              <div className="flex items-center gap-2 px-4 pt-3 border-t" style={{ borderColor: 'var(--border)' }}>
                {socials.map(({ icon: Icon, href, label, hoverColor, hoverBg }) => (
                  <motion.a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}
                    className="w-9 h-9 flex items-center justify-center rounded-xl transition-all"
                    style={{ color: 'var(--text-3)', background: 'var(--bg-card)' }}
                    whileHover={{ scale: 1.1, color: hoverColor, background: hoverBg }}
                    whileTap={{ scale: 0.95 }}>
                    <Icon size={14} />
                  </motion.a>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
